"use client"

import { clientLogos } from "@/lib/data"
import { useEffect, useState, useRef } from "react"
import { motion } from "framer-motion"
import { useLanguage } from "@/lib/i18n"

export function TrustStrip() {
  const { t, locale } = useLanguage()
  const [isVisible, setIsVisible] = useState(false)
  const sectionRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
        }
      },
      { threshold: 0.3, rootMargin: "-40px" }
    )

    const observedElement = sectionRef.current
    if (observedElement) {
      observer.observe(observedElement)
    }

    return () => {
      if (observedElement) {
        observer.unobserve(observedElement)
      }
    }
  }, [])

  return (
    <section
      ref={sectionRef}
      id="trust"
      className="relative py-16 md:py-20 px-6 overflow-hidden bg-[#0F0D0A]"
    >
      {/* Top divider - Gold gradient */}
      <motion.div
        className="absolute top-0 left-1/2 -translate-x-1/2 h-px w-[90%] max-w-[1200px]"
        style={{
          background:
            "linear-gradient(90deg, transparent 0%, rgba(201,162,39,0.3) 50%, transparent 100%)",
        }}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: isVisible ? 1 : 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <motion.p
          className={`text-center mb-10 md:mb-12 text-sm md:text-base text-[#6B6B63]${locale !== "zh" ? " italic" : " font-light"}`}
          style={{
            clipPath: isVisible ? "inset(0 0 0 0)" : "inset(0 100% 0 0)",
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: isVisible ? 0.8 : 0 }}
          transition={{
            clipPath: { duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] },
            opacity: { duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] },
          }}
        >
          {t("trust.tagline")}
        </motion.p>

        {/* Client logos - scrolling */}
        <motion.div
          className="relative overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: isVisible ? 1 : 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="flex animate-scroll-continuous gap-10 md:gap-14">
            {clientLogos.map((logo) => (
              <div
                key={`logo-1-${logo}`}
                className="text-[#A8A8A0] text-sm md:text-base font-medium whitespace-nowrap flex-shrink-0 transition-all duration-300 hover:text-[#C9A227] hover:scale-105"
                style={{
                  opacity: 0.5,
                }}
              >
                {logo}
              </div>
            ))}
            {clientLogos.map((logo) => (
              <div
                key={`logo-2-${logo}`}
                className="text-[#A8A8A0] text-sm md:text-base font-medium whitespace-nowrap flex-shrink-0 transition-all duration-300 hover:text-[#C9A227] hover:scale-105"
                style={{
                  opacity: 0.5,
                }}
              >
                {logo}
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Bottom divider - Gold gradient */}
      <motion.div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 h-px w-[90%] max-w-[1200px]"
        style={{
          background:
            "linear-gradient(90deg, transparent 0%, rgba(201,162,39,0.3) 50%, transparent 100%)",
        }}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: isVisible ? 1 : 0 }}
        transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
      />
    </section>
  )
}
